import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  deleteAgentConversation,
  listAgentConversations,
  loadAgentConversation,
  renameAgentConversation,
  sendAgentMessageStream,
  initAgentActivity,
  nextAgentActivity,
} from '../../../lib/kbAgentApi'
import { btnSecondary } from '../knowledgePageLayout'
import AgentChatThread from './AgentChatThread'
import AgentComposer from './AgentComposer'
import AgentThreadList from './AgentThreadList'
import {
  agentColors,
  agentDrawerWidth,
  bumpConversationToTop,
  glassDrawerShellStyle,
} from './agentChatLayout'

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  )
  useEffect(() => {
    const mq = window.matchMedia(query)
    const handler = () => setMatches(mq.matches)
    mq.addEventListener('change', handler)
    setMatches(mq.matches)
    return () => mq.removeEventListener('change', handler)
  }, [query])
  return matches
}

function newClientMsgId() {
  return `client-${crypto.randomUUID()}`
}

/**
 * Right drawer: history rail + active chat + composer. Anthropic-soft styling.
 */
export default function AgentChatDrawer({
  open,
  onClose,
  clientId: initialClientId,
  includePersonal: initialIncludePersonal,
  allowedClients,
  personalKbAvailable,
  canOpenWiki,
}) {
  const isMobile = useMediaQuery('(max-width: 640px)')
  const [conversations, setConversations] = useState([])
  const [conversationId, setConversationId] = useState(null)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [agentActivity, setAgentActivity] = useState(null)
  const [clientId, setClientId] = useState(initialClientId || '')
  const [includePersonal, setIncludePersonal] = useState(initialIncludePersonal === true)
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [messagesLoading, setMessagesLoading] = useState(false)
  const conversationIdRef = useRef(null)
  const streamingRef = useRef(false)
  const abortControllerRef = useRef(null)
  const refreshTimerRef = useRef(null)
  const deltaBufferRef = useRef('')
  const rafFlushRef = useRef(null)
  const assistantClientIdRef = useRef(null)
  const activityCtxRef = useRef(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!sending && agentActivity) {
      setAgentActivity(null)
      activityCtxRef.current = null
    }
  }, [sending, agentActivity])

  useEffect(() => {
    conversationIdRef.current = conversationId
  }, [conversationId])

  const abortActiveStream = useCallback(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    if (rafFlushRef.current) {
      cancelAnimationFrame(rafFlushRef.current)
      rafFlushRef.current = null
    }
    deltaBufferRef.current = ''
  }, [])

  const refreshConversations = useCallback(async () => {
    const convs = await listAgentConversations()
    if (mountedRef.current) setConversations(convs)
    return convs
  }, [])

  const scheduleRefreshConversations = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null
      refreshConversations().catch(() => {})
    }, 300)
  }, [refreshConversations])

  const bumpActiveThread = useCallback((cid, patch = {}) => {
    if (!cid) return
    setConversations((prev) => bumpConversationToTop(prev, cid, patch))
  }, [])

  const syncActiveConversation = useCallback(async () => {
    const cid = conversationIdRef.current
    if (!cid || !mountedRef.current) return null
    const data = await loadAgentConversation(cid)
    if (mountedRef.current) setMessages(data.messages ?? [])
    return data
  }, [])

  useEffect(() => {
    if (!open) {
      abortActiveStream()
      return
    }
    setHistoryLoading(true)
    let nextClientId = initialClientId || ''
    if (nextClientId && !allowedClients.some((c) => c.id === nextClientId)) {
      nextClientId = ''
    }
    setClientId(nextClientId)
    if (!personalKbAvailable) setIncludePersonal(false)
    else setIncludePersonal(initialIncludePersonal === true)
    let cancelled = false
    ;(async () => {
      setError('')
      try {
        await refreshConversations()
      } catch (e) {
        if (!cancelled) setError(e?.message ?? 'Failed to load chats')
      } finally {
        if (!cancelled) setHistoryLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [
    open,
    initialClientId,
    initialIncludePersonal,
    refreshConversations,
    allowedClients,
    personalKbAvailable,
    abortActiveStream,
  ])

  useEffect(() => {
    if (!open || !personalKbAvailable) return
    setIncludePersonal(initialIncludePersonal === true)
  }, [open, initialIncludePersonal, personalKbAvailable])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  useEffect(() => {
    return () => {
      abortActiveStream()
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    }
  }, [abortActiveStream])

  const flushDeltaBuffer = useCallback(() => {
    rafFlushRef.current = null
    const chunk = deltaBufferRef.current
    if (!chunk || !assistantClientIdRef.current) return
    deltaBufferRef.current = ''
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.client_id === assistantClientIdRef.current)
      if (idx === -1) return prev
      const next = [...prev]
      const row = next[idx]
      next[idx] = { ...row, content: (row.content || '') + chunk }
      return next
    })
  }, [])

  const queueDelta = useCallback(
    (delta) => {
      deltaBufferRef.current += delta
      if (!rafFlushRef.current) {
        rafFlushRef.current = requestAnimationFrame(flushDeltaBuffer)
      }
    },
    [flushDeltaBuffer]
  )

  const loadConversation = async (id) => {
    if (!id || sending) return
    setError('')
    setConversationId(id)
    setMessagesLoading(true)
    try {
      const data = await loadAgentConversation(id)
      setMessages(data.messages ?? [])
      setMobileHistoryOpen(false)
    } catch (e) {
      setError(e?.message ?? 'Failed to load conversation')
    } finally {
      setMessagesLoading(false)
    }
  }

  const handleNewChat = () => {
    abortActiveStream()
    setError('')
    setConversationId(null)
    conversationIdRef.current = null
    setMessages([])
    setAgentActivity(null)
    activityCtxRef.current = null
    setMobileHistoryOpen(false)
  }

  const handleRename = async (id, title) => {
    setError('')
    try {
      await renameAgentConversation(id, title)
      await refreshConversations()
    } catch (e) {
      setError(e?.message ?? 'Failed to rename')
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this chat? This cannot be undone.')) return
    setError('')
    try {
      await deleteAgentConversation(id)
      if (conversationId === id) {
        setConversationId(null)
        setMessages([])
      }
      await refreshConversations()
    } catch (e) {
      setError(e?.message ?? 'Failed to delete')
    }
  }

  const handleClose = () => {
    abortActiveStream()
    onClose()
  }

  const handleStop = useCallback(() => {
    if (rafFlushRef.current) {
      cancelAnimationFrame(rafFlushRef.current)
      rafFlushRef.current = null
    }
    flushDeltaBuffer()
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    deltaBufferRef.current = ''
    setAgentActivity(null)
    activityCtxRef.current = null
    setSending(false)
    streamingRef.current = false
    setMessages((prev) =>
      prev.map((m) => (m.streaming ? { ...m, streaming: false } : m))
    )
    assistantClientIdRef.current = null
  }, [flushDeltaBuffer])

  const handleSend = async () => {
    const text = input.trim()
    if (!text || sending) return

    abortActiveStream()
    const controller = new AbortController()
    abortControllerRef.current = controller

    setSending(true)
    setError('')
    const init = initAgentActivity(text)
    activityCtxRef.current = init.ctx
    setAgentActivity(init.activity)
    setInput('')

    const userClientId = newClientMsgId()
    const userMsg = {
      role: 'user',
      content: text,
      client_id: userClientId,
      created_at: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, userMsg])

    let messageReceived = false
    let deltaReceived = false
    let toolDoneCount = 0
    let toolInFlight = false
    let sawToolStart = false
    streamingRef.current = false
    assistantClientIdRef.current = null
    deltaBufferRef.current = ''

    const events = []

    const applyActivityEvent = (ev) => {
      const { ctx, activity } = nextAgentActivity(activityCtxRef.current, ev)
      activityCtxRef.current = ctx
      if (activity !== undefined) {
        setAgentActivity(activity)
      }
    }

    const ensureAssistantBubble = () => {
      if (assistantClientIdRef.current) return
      const aid = newClientMsgId()
      assistantClientIdRef.current = aid
      streamingRef.current = true
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          client_id: aid,
          content: '',
          metadata: {},
          streaming: true,
          created_at: new Date().toISOString(),
        },
      ])
    }

    try {
      const streamResult = await sendAgentMessageStream(
        {
          message: text,
          conversationId,
          clientId: clientId || undefined,
          includePersonal: personalKbAvailable && includePersonal,
          signal: controller.signal,
        },
        (ev) => {
          if (controller.signal.aborted) return
          events.push(ev)

          if (
            ev.type === 'status' ||
            ev.type === 'tool_start' ||
            ev.type === 'tool_done' ||
            ev.type === 'tool_selected' ||
            ev.type === 'content_start' ||
            ev.type === 'error'
          ) {
            applyActivityEvent(ev)
          }

          if (ev.type === 'content_start') {
            setAgentActivity(null)
          }

          if (ev.type === 'tool_start' && ev.tool) {
            sawToolStart = true
            toolInFlight = true
            deltaBufferRef.current = ''
            if (rafFlushRef.current) {
              cancelAnimationFrame(rafFlushRef.current)
              rafFlushRef.current = null
            }
            if (assistantClientIdRef.current) {
              setMessages((prev) => {
                const idx = prev.findIndex((m) => m.client_id === assistantClientIdRef.current)
                if (idx === -1) return prev
                const row = prev[idx]
                if (!row.streaming) return prev
                assistantClientIdRef.current = null
                streamingRef.current = false
                return prev.filter((_, i) => i !== idx)
              })
            }
          }

          if (ev.type === 'tool_done' && ev.tool) {
            toolDoneCount += 1
            toolInFlight = false
          }

          if (ev.type === 'title' && ev.conversation_id) {
            conversationIdRef.current = ev.conversation_id
            setConversationId(ev.conversation_id)
            bumpActiveThread(ev.conversation_id, { title: ev.title })
          }

          if (ev.type === 'content_delta' && ev.delta) {
            if (messageReceived) return

            const afterTools = sawToolStart && toolDoneCount > 0 && !toolInFlight
            if (afterTools) {
              setAgentActivity(null)
              deltaReceived = true
              ensureAssistantBubble()
              queueDelta(ev.delta)
            }
          }

          if (ev.type === 'message') {
            messageReceived = true
            streamingRef.current = false
            if (rafFlushRef.current) {
              cancelAnimationFrame(rafFlushRef.current)
              rafFlushRef.current = null
            }
            deltaBufferRef.current = ''
            const payload = ev.message ?? ev
            if (ev.conversation_id) {
              conversationIdRef.current = ev.conversation_id
              setConversationId(ev.conversation_id)
              bumpActiveThread(ev.conversation_id, {
                title: ev.title || payload.content?.slice(0, 60),
              })
            }
            setMessages((prev) => {
              const idx = prev.findIndex((m) => m.role === 'assistant' && m.streaming)
              const existing = idx >= 0 ? prev[idx] : null
              const payloadContent = payload.content ?? ''
              const streamedContent = existing?.content ?? ''
              const mergedContent =
                payloadContent.length >= streamedContent.length ? payloadContent : streamedContent
              const merged = {
                ...existing,
                role: 'assistant',
                id: payload.id,
                client_id: existing?.client_id ?? assistantClientIdRef.current ?? newClientMsgId(),
                content: mergedContent || payloadContent,
                metadata: {
                  citations: payload.citations ?? ev.citations,
                  tools_used: payload.tools_used ?? ev.tools_used,
                },
                streaming: false,
                created_at: payload.created_at ?? existing?.created_at ?? new Date().toISOString(),
              }
              if (idx >= 0) {
                const next = [...prev]
                next[idx] = merged
                return next
              }
              return [...prev, merged]
            })
            assistantClientIdRef.current = null
            setAgentActivity(null)
            activityCtxRef.current = null
          }

          if (ev.type === 'error') {
            streamingRef.current = false
            throw Object.assign(new Error(ev.message ?? 'Agent failed'), {
              hintCode: ev.hint_code,
            })
          }
        }
      )

      if (streamResult?.aborted) return

      if (!messageReceived) {
        flushDeltaBuffer()
        setAgentActivity(null)
        activityCtxRef.current = null
      }
      streamingRef.current = false

      const cid = conversationIdRef.current
      if (cid) {
        if (!messageReceived) {
          await syncActiveConversation()
        }
        bumpActiveThread(cid)
        scheduleRefreshConversations()
      }
    } catch (e) {
      if (e?.name === 'AbortError' || controller.signal.aborted) return
      setAgentActivity(null)
      activityCtxRef.current = null
      streamingRef.current = false
      if (deltaBufferRef.current && assistantClientIdRef.current) flushDeltaBuffer()
      if (deltaReceived && assistantClientIdRef.current) {
        setMessages((prev) => {
          const idx = prev.findIndex((m) => m.client_id === assistantClientIdRef.current)
          if (idx === -1) return prev
          const row = prev[idx]
          if (!row.content?.trim()) {
            return prev.filter((_, i) => i !== idx)
          }
          const next = [...prev]
          next[idx] = { ...row, streaming: false }
          return next
        })
      }
      const hint =
        e.hintCode === 'MISSING_LLM_KEY'
          ? ' Configure LLM keys under Settings → Integrations.'
          : e.hintCode === 'RATE_LIMIT'
            ? ' Try again later.'
            : ''
      setError(`${e?.message ?? 'Send failed'}${hint}`)
      setMessages((prev) => prev.filter((m) => m.client_id !== userClientId))
    } finally {
      assistantClientIdRef.current = null
      abortControllerRef.current = null
      if (mountedRef.current) setSending(false)
    }
  }

  if (!open) return null

  const clientLabel = clientId
    ? allowedClients.find((c) => c.id === clientId)?.client_name || clientId
    : 'All clients'

  const headerSub = [
    clientLabel,
    personalKbAvailable && includePersonal ? 'Personal on' : 'Personal off',
  ].join(' · ')

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: agentColors.overlay,
        zIndex: 1100,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
      onClick={handleClose}
      role="presentation"
    >
      <div
        style={{
          width: isMobile ? '100%' : agentDrawerWidth,
          maxWidth: '100vw',
          height: '100%',
          ...glassDrawerShellStyle,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Knowledge Assistant chat"
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 16px',
            borderBottom: '1px solid rgba(255,255,255,0.45)',
            background: 'rgba(255,255,255,0.35)',
            backdropFilter: 'blur(12px)',
            flexShrink: 0,
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: agentColors.textPrimary }}>
              Knowledge Assistant
            </div>
            {isMobile ? (
              <div style={{ fontSize: 12, color: agentColors.textSecondary, marginTop: 2 }}>
                {headerSub}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            aria-label="Close chat"
            onClick={handleClose}
            style={{
              ...btnSecondary,
              padding: '6px 12px',
              fontSize: 18,
              lineHeight: 1,
              border: 'none',
              background: 'transparent',
              color: agentColors.textSecondary,
            }}
          >
            ×
          </button>
        </div>

        {isMobile ? (
          <div
            style={{
              display: 'flex',
              gap: 8,
              padding: '8px 12px',
              borderBottom: '1px solid rgba(255,255,255,0.45)',
              background: 'rgba(255,255,255,0.25)',
            }}
          >
            <button
              type="button"
              style={{ ...btnSecondary, flex: 1, fontSize: 13, background: 'rgba(255,255,255,0.5)' }}
              onClick={() => setMobileHistoryOpen((v) => !v)}
            >
              Chats ▾
            </button>
            <button
              type="button"
              style={{ ...btnSecondary, fontSize: 13, background: 'rgba(255,255,255,0.5)' }}
              onClick={handleNewChat}
            >
              + New
            </button>
          </div>
        ) : null}

        {isMobile && mobileHistoryOpen ? (
          <div
            style={{
              maxHeight: 220,
              overflowY: 'auto',
              borderBottom: '1px solid rgba(255,255,255,0.45)',
            }}
          >
            <AgentThreadList
              mobile
              loading={historyLoading}
              conversations={conversations}
              activeId={conversationId}
              onSelect={loadConversation}
              onNewChat={handleNewChat}
              onRename={handleRename}
              onDelete={handleDelete}
            />
          </div>
        ) : null}

        <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
          {!isMobile ? (
            <AgentThreadList
              loading={historyLoading}
              conversations={conversations}
              activeId={conversationId}
              onSelect={loadConversation}
              onNewChat={handleNewChat}
              onRename={handleRename}
              onDelete={handleDelete}
            />
          ) : null}

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {!isMobile ? (
              <div
                style={{
                  padding: '12px 20px',
                  borderBottom: '1px solid rgba(255,255,255,0.45)',
                  background: 'rgba(255,255,255,0.28)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 600, color: agentColors.textPrimary }}>
                  {conversations.find((c) => c.id === conversationId)?.title || 'New conversation'}
                </div>
                <div style={{ fontSize: 12, color: agentColors.textSecondary, marginTop: 2 }}>
                  {headerSub}
                </div>
              </div>
            ) : null}

            <AgentChatThread
              messages={messages}
              agentActivity={agentActivity}
              canOpenWiki={canOpenWiki}
              sending={sending}
              messagesLoading={messagesLoading}
            />

            {error ? (
              <div
                style={{
                  margin: '0 16px 8px',
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: 'rgba(254,242,242,0.85)',
                  border: '1px solid #fecaca',
                  color: agentColors.danger,
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            ) : null}

            <AgentComposer
              input={input}
              setInput={setInput}
              onSend={handleSend}
              onStop={handleStop}
              sending={sending}
              clientId={clientId}
              setClientId={setClientId}
              clients={allowedClients}
              includePersonal={includePersonal}
              setIncludePersonal={setIncludePersonal}
              personalKbAvailable={personalKbAvailable}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
